import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Box,
  Flex,
  Button,
  Select,
  Heading,
  Text,
  HStack,
  VStack,
  useDisclosure,
  useColorModeValue,
  Tooltip,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, ChevronUpIcon, CalendarIcon } from '@chakra-ui/icons';
import { Calendar, Tag, Zap, User, Users, Building } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useRocks } from './useRocks';
import { RockCardDetailed } from './components/RockCardDetailed';
import { RockSummaryTable } from './components/RockSummaryTable';
import { RockFormModal } from './components/RockFormModal';
import { RockIssuesModal } from './components/RockIssuesModal';
import { AchieveRockModal } from './components/AchieveRockModal';
import { DropRockModal } from './components/DropRockModal';
import { ConfirmModal } from './components/ConfirmModal';
import { AITermsModal } from './components/AITermsModal';
import { RockEvaluationLoadingModal } from './components/RockEvaluationLoadingModal';
import { RockEvaluationResultsModal } from './components/RockEvaluationResultsModal';
import { RockRewriteComparisonModal } from './components/RockRewriteComparisonModal';
import { RockChatModal } from './components/RockChatModal';
import { PIIWarningModal } from './components/PIIWarningModal';
import { Rock, Issue, ActionItem } from '../types';
import { calculateAverageScore, getOpenIssuesForRock, getComputedRockStatus } from './rocks.util';
import { OutstandingIssuesModal } from '../L10Meeting/components/OutstandingIssuesModal';
import { AddIssueModal } from '../Issues/components/AddIssueModal';

type ViewMode = 'detailed' | 'summary';

/**
 * Rocks Page - Main page for viewing and managing Rocks
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function RocksPage() {
  const history = useHistory();
  const { currentUser } = useAuth();
  const {
    rocks,
    users,
    departments,
    loading,
    rockAiScores,
    createRock,
    updateRock,
    deleteRock,
    achieveRock,
    dropRock,
    loadMilestonesForRock,
    loadActionItemsForRock,
    loadIssuesForRock,
    subscribeToActionItems,
    subscribeToMilestonesForRock,
    subscribeToActionItemsForRock,
    subscribeToIssues,
    addIssue,
    resolveIssue,
    toggleActionItem,
    addActionItem,
    addMilestone,
    updateMilestone,
    toggleMilestone,
    updateActionItem,
    updateIssue,
    saveRockUpdate,
    loadPreviousNotes,
    checkAITerms,
    acceptAITerms,
    evaluateRock,
    getLatestEvaluation,
    saveEvaluation,
    actuallyResolveIssue,
    acknowledgeSuggestion,
  } = useRocks(currentUser?.uid);

  const [viewMode, setViewMode] = useState<ViewMode>('detailed');
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [collaboratorFilter, setCollaboratorFilter] = useState<string>('all');
  const [rockNameFilter, setRockNameFilter] = useState<string>('all');
  const [rockTypeFilter, setRockTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [editingRock, setEditingRock] = useState<Rock | null>(null);
  const [expandedRocks, setExpandedRocks] = useState<Record<string, boolean>>({});
  const [selectedRockId, setSelectedRockId] = useState<string | null>(null);
  const [selectedRockForIssues, setSelectedRockForIssues] = useState<string | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  
  // Refresh triggers
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [refreshMilestonesTrigger, setRefreshMilestonesTrigger] = useState(0);
  const [refreshActionItemsTrigger, setRefreshActionItemsTrigger] = useState(0);

  const [showOutstandingIssuesModal, setShowOutstandingIssuesModal] = useState(false);
  const [showAddIssueModal, setShowAddIssueModal] = useState(false);
  const [addIssueModalButtonText, setAddIssueModalButtonText] = useState<string>('Save Issue');
  const [rockForIssueRequired, setRockForIssueRequired] = useState<{ id: string; name: string } | null>(null);
  const [addIssueModalContext, setAddIssueModalContext] = useState<'moving-to-off-track' | 'adding-to-on-track' | undefined>(undefined);
  const [pendingRockStatusChange, setPendingRockStatusChange] = useState<{
    rockId: string;
    newStatus: 'on-track' | 'off-track';
  } | null>(null);
  const [outstandingIssuesCount, setOutstandingIssuesCount] = useState(0);
  const toast = useToast();

  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose,
  } = useDisclosure();

  const {
    isOpen: isAchieveOpen,
    onOpen: onAchieveOpen,
    onClose: onAchieveClose,
  } = useDisclosure();

  const {
    isOpen: isDropOpen,
    onOpen: onDropOpen,
    onClose: onDropClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const {
    isOpen: isIssuesOpen,
    onOpen: onIssuesOpen,
    onClose: onIssuesClose,
  } = useDisclosure();

  // AI Evaluation modals
  const [selectedRockForEval, setSelectedRockForEval] = useState<Rock | null>(null);
  const [evaluationResult, setEvaluationResult] = useState<any>(null);
  const [piiDetectionResult, setPiiDetectionResult] = useState<any>(null);
  const [evaluatingRockId, setEvaluatingRockId] = useState<string | null>(null);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean>(false);

  const { isOpen: isTermsOpen, onOpen: onTermsOpen, onClose: onTermsClose } = useDisclosure();
  const { isOpen: isLoadingOpen, onOpen: onLoadingOpen, onClose: onLoadingClose } = useDisclosure();
  const { isOpen: isResultsOpen, onOpen: onResultsOpen, onClose: onResultsClose } = useDisclosure();
  const { isOpen: isRewriteOpen, onOpen: onRewriteOpen, onClose: onRewriteClose } = useDisclosure();
  const { isOpen: isChatOpen, onOpen: onChatOpen, onClose: onChatClose } = useDisclosure();
  const { isOpen: isPIIWarningOpen, onOpen: onPIIWarningOpen, onClose: onPIIWarningClose } = useDisclosure();

  // Load issues and action items for a rock
  const loadRockIssuesData = useCallback(async (rockId: string) => {
    if (!loadIssuesForRock || !loadActionItemsForRock) return;
    try {
      const [issuesData, actionItemsData] = await Promise.all([
        loadIssuesForRock(rockId),
        loadActionItemsForRock(rockId),
      ]);
      setIssues(issuesData);
      setActionItems(actionItemsData);
    } catch (error) {
      console.error('Error loading rock issues data:', error);
    }
  }, [loadIssuesForRock, loadActionItemsForRock]);

  // Hash-based navigation with filter clearing and highlighting
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#rocks/')) {
        const rockId = hash.replace('#rocks/', '');

        // Clear all filters to ensure the rock is visible
        setOwnerFilter('all');
        setCollaboratorFilter('all');
        setRockNameFilter('all');
        setRockTypeFilter('all');
        setStatusFilter('all');
        setDepartmentFilter('all');

        // Expand the rock
        setExpandedRocks((prev) => ({ ...prev, [rockId]: true }));

        // Scroll to the rock with a delay to ensure rendering
        setTimeout(() => {
          const element = document.getElementById(`rock-${rockId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a highlight effect
            element.classList.add('ring-4', 'ring-blue-500', 'ring-opacity-50');
            setTimeout(() => {
              element.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-50');
            }, 2000);
          }
        }, 300);
      }
    };

    // Handle initial hash on mount or when rocks change
    handleHashNavigation();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashNavigation);

    return () => {
      window.removeEventListener('hashchange', handleHashNavigation);
    };
  }, [rocks]);



  // Note: AI scores are loaded and managed by useRocks hook via rockEvaluations

  // Set up real-time subscriptions for action items
  // Use refs to prevent re-subscriptions when selectedRockForIssues or loadRockIssuesData change
  const selectedRockForIssuesRef = useRef<string | null>(null);
  const loadRockIssuesDataRef = useRef(loadRockIssuesData);
  
  // Update refs when values change (without triggering re-subscription)
  selectedRockForIssuesRef.current = selectedRockForIssues;
  loadRockIssuesDataRef.current = loadRockIssuesData;

  useEffect(() => {
    if (rocks.length > 0 && subscribeToActionItems) {
      const rockIds = rocks.map((r) => r.id);
      const unsubscribe = subscribeToActionItems(rockIds, () => {
        // Refresh data when action items change
        // Only refresh action items, not everything
        setRefreshActionItemsTrigger((prev) => prev + 1);
        
        // Use refs to get current values without causing re-subscription
        if (selectedRockForIssuesRef.current && loadRockIssuesDataRef.current) {
          loadRockIssuesDataRef.current(selectedRockForIssuesRef.current);
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, [rocks, subscribeToActionItems]);

  const handleOpenModal = useCallback((rock?: Rock) => {
    setEditingRock(rock || null);
    onFormOpen();
  }, [onFormOpen]);



  const handleDeleteRock = useCallback((rockId: string) => {
    setSelectedRockId(rockId);
    onDeleteOpen();
  }, [onDeleteOpen]);

  const handleConfirmDelete = useCallback(async () => {
    if (selectedRockId) {
      await deleteRock(selectedRockId);
      setSelectedRockId(null);
      onDeleteClose();
    }
  }, [selectedRockId, deleteRock, onDeleteClose]);

  const handleAchieveRock = useCallback((rockId: string) => {
    setSelectedRockId(rockId);
    onAchieveOpen();
  }, [onAchieveOpen]);

  const handleConfirmAchieve = useCallback(async () => {
    if (selectedRockId) {
      await achieveRock(selectedRockId);
      setSelectedRockId(null);
      onAchieveClose();
    }
  }, [selectedRockId, achieveRock, onAchieveClose]);

  const handleDropRock = useCallback((rockId: string) => {
    setSelectedRockId(rockId);
    onDropOpen();
  }, [onDropOpen]);

  const handleConfirmDrop = useCallback(async () => {
    if (selectedRockId) {
      await dropRock(selectedRockId);
      setSelectedRockId(null);
      onDropClose();
    }
  }, [selectedRockId, dropRock, onDropClose]);

  const handleIssueClick = useCallback(async (rockId: string, hasOpenIssues?: boolean) => {
    // Determine if rock has open issues
    // Use passed value if available, otherwise fetch
    let hasIssues = false;
    const rock = rocks.find((r) => r.id === rockId);
    
    if (hasOpenIssues !== undefined) {
      hasIssues = hasOpenIssues;
    } else {
      const openIssues = await getOpenIssuesForRock(rockId);
      hasIssues = openIssues.length > 0;
    }
    
    if (!hasIssues && rock) {
      // Rock is on-track - open AddIssueModal with context
      setRockForIssueRequired({ id: rockId, name: rock.rock });
      setAddIssueModalContext('adding-to-on-track');
      setAddIssueModalButtonText('Add Issue & Move Rock to Off-Track');
      setShowAddIssueModal(true);
    } else {
      // Rock has issues - open RockIssuesModal to view existing issues
      setSelectedRockForIssues(rockId);
      onIssuesOpen();
    }
  }, [onIssuesOpen, rocks]);

  const handleAddIssue = async (rockId: string, name: string, description: string) => {
    if (!currentUser?.uid) return;
    await addIssue(rockId, name, description, currentUser.uid);
    if (selectedRockForIssues) {
      await loadRockIssuesData(selectedRockForIssues);
    }
    // Issues will update automatically via snapshot listener
  };

  const handleResolveIssue = async (issueId: string, resolutionNotes?: string) => {
    try {
      const result = await resolveIssue(issueId, resolutionNotes);
      
      // If it's the last issue, resolveIssue returns early without resolving
      // The RockCardDetailed component handles this case with its inline modal
      // For RockIssuesModal, we'll just proceed - the issue will be resolved
      // when the user confirms in the RockCardDetailed modal
      if (result.isLastIssue) {
        // Issue not resolved yet - will be handled by RockCardDetailed's modal
        // Just reload issues data
        if (selectedRockForIssues) {
          await loadRockIssuesData(selectedRockForIssues);
        }
        return;
      }

      // Normal flow - not the last issue, already resolved
      // Reload issues data only for the modal if it's open
      if (selectedRockForIssues) {
        await loadRockIssuesData(selectedRockForIssues);
      }
      
      // Issues will update automatically via snapshot listener
      
    } catch (error) {
      console.error('Error resolving issue:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to resolve issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handler for inline issue resolution from RockCardDetailed
  // Returns promise that resolves when issue is handled
  const handleResolveIssueInline = useCallback(async (issueId: string, resolutionNotes: string, relatedRockId: string): Promise<void> => {
    try {
      // Attempt to resolve. resolveIssue will return early with isLastIssue: true 
      // if it's the last issue, without resolving it.
      const result = await resolveIssue(issueId, resolutionNotes);
      
      // If it was the last issue, we know the user has already confirmed via the 
      // smart modal in RockCardDetailed (which handles the UI logic), so we
      // proceed to actually resolve it.
      if (result.isLastIssue) {
        await actuallyResolveIssue(issueId, resolutionNotes);
      }
      
      // Issues will update automatically via snapshot listener
      
    } catch (error) {
      console.error('Error resolving issue:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to resolve issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error; // Re-throw so component can handle it
    }
  }, [resolveIssue, actuallyResolveIssue, toast]);

  const handleToggleActionItem = async (actionItemId: string, complete: boolean) => {
    await toggleActionItem(actionItemId, complete);
    if (selectedRockForIssues) {
      await loadRockIssuesData(selectedRockForIssues);
    }
    // Only refresh action items
    setRefreshActionItemsTrigger((prev) => prev + 1);
  };

  const handleAddActionItem = async (
    rockId: string,
    task: string,
    assigneeId: string | null,
    dueDate: string,
    issueId?: string
  ) => {
    await addActionItem(rockId, task, assigneeId, dueDate, issueId);
    if (selectedRockForIssues) {
      await loadRockIssuesData(selectedRockForIssues);
    }
    // Only refresh action items
    setRefreshActionItemsTrigger((prev) => prev + 1);
  };

  // Handler for milestone changes
  const handleAddMilestone = async (rockId: string, task: string, dueDate: string, sortOrder: number) => {
    await addMilestone(rockId, task, dueDate, sortOrder);
    // Refresh milestones only
    setRefreshMilestonesTrigger((prev) => prev + 1);
  };

  const handleToggleMilestone = async (milestoneId: string, complete: boolean) => {
    await toggleMilestone(milestoneId, complete);
    // Refresh milestones only
    setRefreshMilestonesTrigger((prev) => prev + 1);
  };

  const handleAddIssueFromStatusChange = async (formData: any) => {
    if (!rockForIssueRequired) return;
    
    const relatedRockId = formData.relatedRockId || rockForIssueRequired.id;
    
    try {
      // Create issue using addIssue from useRocks hook
      await addIssue(relatedRockId, formData.name, formData.description, currentUser?.uid || '');
      
      // Issue creation automatically makes rock off-track (computed from issues)
      const wasFromOffTrackClick = pendingRockStatusChange?.newStatus === 'off-track';
      
      toast({
        title: 'Success',
        description: wasFromOffTrackClick 
          ? 'Issue created. Rock is now off-track.' 
          : 'Issue created successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setShowAddIssueModal(false);
      setRockForIssueRequired(null);
      setPendingRockStatusChange(null);
      setAddIssueModalContext(undefined);
      
      // Refresh issues if modal is open
      if (selectedRockForIssues) {
        await loadRockIssuesData(selectedRockForIssues);
      }
      
      // Issues will update automatically via snapshot listener
    } catch (error) {
      console.error('Error creating issue:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };


  // AI Evaluation handlers with enhanced error handling
  const runEvaluation = useCallback(async (rockId: string, skipLoadingModal = false) => {
    setEvaluatingRockId(rockId);
    if (!skipLoadingModal) {
      onLoadingOpen();
    }
    try {
      const result = await evaluateRock(rockId);
      setEvaluationResult(result);
      
      // Save evaluation to database
      const rock = rocks.find((r) => r.id === rockId);
      if (rock && saveEvaluation) {
        await saveEvaluation(rockId, result, {
          rock: rock.rock,
          measurable: rock.measurable,
          due_date: rock.dueDate,
          status: rock.status,
          progress: rock.progress,
        });
      }
      
      // Note: AI score will be updated via rockEvaluations in useRocks hook
      
      // Verify terms are still accepted before showing results
      const termsAccepted = await checkAITerms();
      setHasAcceptedTerms(termsAccepted);
      
      onLoadingClose();
      onResultsOpen();
    } catch (error: any) {
      onLoadingClose();
      
      // Handle specific error codes
      const errorCode = error?.code || error?.error?.code;
      const errorMessage = error?.message || error?.error?.message || 'Failed to evaluate rock';
      
      if (errorCode === 'invalid-argument' && errorMessage.includes('PII')) {
        // PII detected - show warning modal
        setPiiDetectionResult({
          hasPII: true,
          types: ['sensitive_data'],
          severity: 'critical',
        });
        onPIIWarningOpen();
      } else if (errorCode === 'resource-exhausted' || errorMessage.includes('rate limit')) {
        toast({
          title: 'Rate Limit Exceeded',
          description: 'You have exceeded the rate limit for AI evaluations. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else if (errorCode === 'invalid-argument' && errorMessage.includes('injection')) {
        toast({
          title: 'Security Issue',
          description: 'Suspicious content detected. Please remove and try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Evaluation Failed',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      
      console.error('Evaluation failed:', error);
      setEvaluatingRockId(null);
    }
  }, [evaluateRock, onLoadingOpen, onLoadingClose, onResultsOpen, rocks, saveEvaluation, toast, onPIIWarningOpen, checkAITerms]);

  const handleSaveRock = useCallback(async (formData: any) => {
    try {
      if (editingRock) {
        await updateRock(editingRock.id, formData);
        onFormClose();
        setEditingRock(null);
      } else {
        // Create new rock and get the ID
        const newRockRef = await createRock(formData);
        const newRockId = newRockRef?.id || (newRockRef as any);
        
        onFormClose();
        setEditingRock(null);

        // Auto-run AI Evaluation for new rocks
        if (newRockId && typeof newRockId === 'string') {
          toast({
            title: 'Rock Created',
            description: 'AI is reviewing your new Rock...',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          
          // Small delay to ensure Firestore propagation
          setTimeout(() => {
            // Check terms silently first
            checkAITerms().then(termsAccepted => {
              if (termsAccepted) {
                 runEvaluation(newRockId, true); // true = skip loading modal
              }
            });
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error saving rock:', error);
      throw error;
    }
  }, [editingRock, updateRock, createRock, onFormClose, runEvaluation, checkAITerms, toast]);

  const handleEvaluateClick = useCallback(async (rock: Rock) => {
    setSelectedRockForEval(rock);
    // Open loading modal immediately for better UX
    setEvaluatingRockId(rock.id);
    onLoadingOpen();
    
    // Check terms in the background
    const termsAccepted = await checkAITerms();
    setHasAcceptedTerms(termsAccepted);
    
    if (termsAccepted) {
      // Terms accepted - proceed with evaluation (loading modal already open)
      await runEvaluation(rock.id, true); // Skip opening loading modal since it's already open
    } else {
      // Terms not accepted - close loading modal and show terms modal
      onLoadingClose();
      setEvaluatingRockId(null);
      onTermsOpen();
    }
  }, [checkAITerms, onTermsOpen, onLoadingOpen, onLoadingClose, runEvaluation]);

  const handleTermsAccepted = async () => {
    try {
      const success = await acceptAITerms();
      if (success && selectedRockForEval) {
        setHasAcceptedTerms(true);
        onTermsClose();
        runEvaluation(selectedRockForEval.id);
      } else {
        throw new Error('Failed to accept AI terms');
      }
    } catch (error: any) {
      console.error('Error accepting AI terms:', error);
      const errorMessage = error?.message || 'Failed to accept AI terms. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleShowRewrite = async () => {
    onResultsClose();
    onRewriteOpen();
    
    // Acknowledge that the user has viewed the AI suggestions
    if (selectedRockForEval?.id && acknowledgeSuggestion) {
      try {
        await acknowledgeSuggestion(selectedRockForEval.id);
      } catch (error) {
        // Silently fail - acknowledgement is non-critical
        console.error('Error acknowledging suggestion:', error);
      }
    }
  };

  const handleApplySuggestion = async () => {
    if (!evaluationResult?.ai_rewrite_rock || !evaluationResult?.ai_rewrite_measurable || !selectedRockForEval) {
      return;
    }

    try {
      await updateRock(selectedRockForEval.id, {
        rock: evaluationResult.ai_rewrite_rock,
        measurable: evaluationResult.ai_rewrite_measurable,
      });
      
      // Force a refresh by incrementing refreshTrigger to ensure UI updates
      // Even though Firestore listeners should update automatically, this ensures
      // the rock cards re-render with the new data
      setRefreshTrigger((prev) => prev + 1);
      
      toast({
        title: 'Rock Updated',
        description: 'AI suggestions have been applied to your rock.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onRewriteClose();
      setSelectedRockForEval(null);
      setEvaluationResult(null);
    } catch (error) {
      console.error('Error applying suggestion:', error);
      toast({
        title: 'Error',
        description: 'Failed to apply AI suggestions. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleKeepOriginal = () => {
    onRewriteClose();
    onResultsOpen();
  };

  const handleOpenChat = () => {
    onResultsClose();
    onChatOpen();
  };



  const toggleRockExpanded = useCallback((rockId: string) => {
    setExpandedRocks((prev) => ({
      ...prev,
      [rockId]: prev[rockId] === undefined ? false : !prev[rockId],
    }));
  }, []);

  const expandAll = () => {
    const allExpanded: Record<string, boolean> = {};
    filteredRocks.forEach((rock) => {
      allExpanded[rock.id] = true;
    });
    setExpandedRocks(allExpanded);
  };

  const collapseAll = () => {
    const allCollapsed: Record<string, boolean> = {};
    filteredRocks.forEach((rock) => {
      allCollapsed[rock.id] = false;
    });
    setExpandedRocks(allCollapsed);
  };

  const isRockExpanded = (rockId: string): boolean => {
    return expandedRocks[rockId] === undefined ? true : expandedRocks[rockId];
  };

  // Helper functions for showing modals
  const showOutstandingIssues = useCallback((issueCount: number) => {
    setOutstandingIssuesCount(issueCount);
    setShowOutstandingIssuesModal(true);
  }, []);

  const handleStatusChange = useCallback(async (
    rockId: string,
    status: 'on-track' | 'off-track'
  ) => {
    const rock = rocks.find((r) => r.id === rockId);
    if (!rock) return;

    // Get open issues count to determine current computed status
    const openIssues = await getOpenIssuesForRock(rockId);
    
    // Handle UI flow based on requested status change
    if (status === 'off-track') {
      // If rock has no issues, show Add Issue modal with special button text
      if (openIssues.length === 0) {
        setRockForIssueRequired({ id: rockId, name: rock.rock });
        setPendingRockStatusChange({ rockId, newStatus: 'off-track' });
        setAddIssueModalButtonText('Add Issue and Move Rock to Off-Track');
        setAddIssueModalContext('moving-to-off-track');
        setShowAddIssueModal(true);
      }
      // If rock already has issues, it's already off-track (computed) - no-op
    } else if (status === 'on-track') {
      // If rock has open issues, show OutstandingIssuesModal
      if (openIssues.length > 0) {
        showOutstandingIssues(openIssues.length);
      }
      // If rock has no issues, it's already on-track (computed) - no-op
    }
  }, [rocks, showOutstandingIssues]);

  // Filter rocks
  const ownerFilteredRocks = rocks.filter((rock) => {
    if (ownerFilter === 'all') return true;
    return rock.ownerId === ownerFilter;
  });

  const collaboratorFilteredRocks = ownerFilteredRocks.filter((rock) => {
    if (collaboratorFilter === 'all') return true;
    return rock.collaborators?.includes(collaboratorFilter);
  });

  const typeFilteredRocks = collaboratorFilteredRocks.filter((rock) => {
    if (rockTypeFilter === 'all') return true;
    return rock.rockType === rockTypeFilter;
  });

  // Note: Status filter uses stored rock.status field
  // In the future, this could be updated to use computed status from issues
  // For now, status is computed in components for display purposes
  const statusFilteredRocks = typeFilteredRocks.filter((rock) => {
    if (statusFilter === 'all') return true;
    return rock.status === statusFilter;
  });

  const departmentFilteredRocks = statusFilteredRocks.filter((rock) => {
    if (departmentFilter === 'all') return true;
    return rock.departmentId === departmentFilter;
  });

  const availableRockNames = Array.from(
    new Set(departmentFilteredRocks.map((rock) => rock.rock))
  ).sort();

  const filteredRocks = departmentFilteredRocks.filter((rock) => {
    if (rockNameFilter === 'all') return true;
    return rock.rock === rockNameFilter;
  });

  // Calculate average score for filtered rocks
  const averageScore = useMemo(() => {
    return calculateAverageScore(filteredRocks, rockAiScores);
  }, [filteredRocks, rockAiScores]);
  // Color mode values for dark mode support (must be before any early returns)
  const emptyBg = useColorModeValue('white', 'gray.700');
  const emptyBorder = useColorModeValue('gray.200', 'gray.600');
  const emptyText = useColorModeValue('gray.500', 'gray.400');
  const emptyTextLight = useColorModeValue('gray.400', 'gray.500');
  const toggleContainerBg = useColorModeValue('gray.100', 'gray.800');
  const toggleActiveBg = useColorModeValue('white', 'gray.700');
  const toggleActiveColor = useColorModeValue('gray.900', 'white');
  const toggleInactiveColor = useColorModeValue('gray.600', 'gray.400');
  const toggleHoverBg = useColorModeValue('gray.200', 'gray.700');

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text>Loading rocks...</Text>
      </Container>
    );
  }

  const selectedRock = selectedRockForIssues
    ? rocks.find((r) => r.id === selectedRockForIssues)
    : null;

  return (
    <Container maxW="container.xl" py={8}>
      <Box 
        mb={2}
        pb={1}
        pt={1}
      >
        {/* Top Row */}
        <Flex justify="space-between" align="flex-end" mb={3} flexWrap="wrap" gap={4}>
          <Flex align="center" gap={4}>
            <Heading size="lg">
              {`All Rocks - ${viewMode === 'detailed' ? 'Detailed View' : 'Summary View'}`}
            </Heading>
            {viewMode === 'detailed' && filteredRocks.length > 0 && averageScore !== null && (
              <Box
                px={3}
                py={1.5}
                bg="red.100"
                border="1px"
                borderColor="red.300"
                borderRadius="md"
                display="inline-flex"
                alignItems="center"
                gap={1}
              >
                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                  AI Score Avg:
                </Text>
                <Text fontSize="sm" fontWeight="bold" color="red.600">
                  {averageScore}/100
                </Text>
              </Box>
            )}
          </Flex>
          <HStack spacing={2} flexShrink={0} flexWrap="wrap" align="flex-end">


            {/* Add button */}
            <Box minW="180px" flexShrink={0}>
              <Button
                leftIcon={<AddIcon />}
                colorScheme="blue"
                onClick={() => handleOpenModal()}
                w="full"
              >
                Add Rock
              </Button>
            </Box>

            {/* Filter Dropdowns */}
            <>
                <Box textAlign="center">
                  <HStack spacing={1} align="center" justify="center" mb={0}>
                    <Icon as={Tag} w={3} h={3} color="gray.600" />
                    <Text fontSize="sm">
                      Type:
                    </Text>
                  </HStack>
                  <Select
                    value={rockTypeFilter}
                    onChange={(e) => setRockTypeFilter(e.target.value)}
                    size="sm"
                    minW="140px"
                    mt={0}
                  >
                    <option value="all">All Types</option>
                    <option value="individual">Individual</option>
                    <option value="company">Company</option>
                  </Select>
                </Box>

                <Box textAlign="center">
                  <HStack spacing={1} align="center" justify="center" mb={0}>
                    <Icon as={Zap} w={3} h={3} color="gray.600" />
                    <Text fontSize="sm">
                      Status:
                    </Text>
                  </HStack>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    size="sm"
                    minW="140px"
                    mt={0}
                  >
                    <option value="all">All Statuses</option>
                    <option value="on-track">On Track</option>
                    <option value="off-track">Off Track</option>
                  </Select>
                </Box>

                <Box textAlign="center">
                  <HStack spacing={1} align="center" justify="center" mb={0}>
                    <Icon as={User} w={3} h={3} color="gray.600" />
                    <Text fontSize="sm">
                      Owner:
                    </Text>
                  </HStack>
                  <Select
                    value={ownerFilter}
                    onChange={(e) => setOwnerFilter(e.target.value)}
                    size="sm"
                    minW="140px"
                    mt={0}
                  >
                    <option value="all">All Owners</option>
                    {users
                      .filter((user) => user.isEOSUser)
                      .map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                  </Select>
                </Box>

                <Box textAlign="center">
                  <HStack spacing={1} align="center" justify="center" mb={0}>
                    <Icon as={Users} w={3} h={3} color="gray.600" />
                    <Text fontSize="sm">
                      Collaborator:
                    </Text>
                  </HStack>
                  <Select
                    value={collaboratorFilter}
                    onChange={(e) => setCollaboratorFilter(e.target.value)}
                    size="sm"
                    minW="160px"
                    mt={0}
                  >
                    <option value="all">All Collaborators</option>
                    {users
                      .filter((user) => user.isEOSUser)
                      .map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                  </Select>
                </Box>

                <Box textAlign="center">
                  <HStack spacing={1} align="center" justify="center" mb={0}>
                    <Icon as={Building} w={3} h={3} color="gray.600" />
                    <Text fontSize="sm">
                      Department:
                    </Text>
                  </HStack>
                  <Select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    size="sm"
                    minW="160px"
                    mt={0}
                  >
                    <option value="all">All Departments</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </Select>
                </Box>
            </>
          </HStack>
        </Flex>

        {/* Bottom Row */}
        <Flex gap={3} wrap="wrap" mb={3} justify="space-between" align="center">
            <HStack spacing={2} align="center">
              <Text fontSize="sm">
                Name:
              </Text>
              <Select
                value={rockNameFilter}
                onChange={(e) => setRockNameFilter(e.target.value)}
                size="sm"
                width="200px"
              >
                <option value="all">All Rocks</option>
                {availableRockNames.map((rockName) => (
                  <option key={rockName} value={rockName}>
                    {rockName}
                  </option>
                ))}
              </Select>

              <Button
                leftIcon={<Icon as={Calendar} w={4} h={4} />}
                variant="outline"
                size="sm"
                colorScheme="gray"
                onClick={() => history.push('/eos/historical-rocks')}
              >
                Historical Rocks
              </Button>

              {/* View mode toggle */}
              <HStack spacing={0} bg={toggleContainerBg} borderRadius="lg" p={0.5}>
                <Box
                  as="button"
                  onClick={() => setViewMode('detailed')}
                  px={3.5}
                  py={1.75}
                  fontSize="sm"
                  fontWeight="medium"
                  borderRadius="md"
                  color={viewMode === 'detailed' ? toggleActiveColor : toggleInactiveColor}
                  bg={viewMode === 'detailed' ? toggleActiveBg : 'transparent'}
                  transition="all 0.2s"
                  _hover={{ bg: viewMode === 'detailed' ? toggleActiveBg : toggleHoverBg }}
                  _focus={{ outline: '2px solid', outlineColor: 'blue.500', outlineOffset: '2px' }}
                >
                  Detailed View
                </Box>
                <Box
                  as="button"
                  onClick={() => setViewMode('summary')}
                  px={3.5}
                  py={1.75}
                  fontSize="sm"
                  fontWeight="medium"
                  borderRadius="md"
                  color={viewMode === 'summary' ? toggleActiveColor : toggleInactiveColor}
                  bg={viewMode === 'summary' ? toggleActiveBg : 'transparent'}
                  transition="all 0.2s"
                  _hover={{ bg: viewMode === 'summary' ? toggleActiveBg : toggleHoverBg }}
                  _focus={{ outline: '2px solid', outlineColor: 'blue.500', outlineOffset: '2px' }}
                >
                  Summary View
                </Box>
              </HStack>
            </HStack>

            <HStack spacing={2}>
              {/* Expand/Collapse buttons */}
              <Button
                leftIcon={<ChevronDownIcon />}
                variant="outline"
                size="sm"
                onClick={expandAll}
                isDisabled={viewMode !== 'detailed' || filteredRocks.length === 0}
                opacity={viewMode !== 'detailed' || filteredRocks.length === 0 ? 0.5 : 1}
              >
                Expand All
              </Button>
              <Button
                leftIcon={<ChevronUpIcon />}
                variant="outline"
                size="sm"
                onClick={collapseAll}
                isDisabled={viewMode !== 'detailed' || filteredRocks.length === 0}
                opacity={viewMode !== 'detailed' || filteredRocks.length === 0 ? 0.5 : 1}
              >
                Collapse All
              </Button>
            </HStack>
        </Flex>

      </Box>

      {filteredRocks.length === 0 ? (
        <Box textAlign="center" py={12} bg={emptyBg} borderRadius="lg" border="1px" borderColor={emptyBorder}>
          <Text color={emptyText} fontSize="lg" mb={2}>
            No rocks found
          </Text>
          <Text color={emptyTextLight} fontSize="sm" mb={4}>
            Create your first 90-day goal to get started
          </Text>
          <Button colorScheme="blue" onClick={() => handleOpenModal()}>
            Add Your First Rock
          </Button>
        </Box>
      ) : viewMode === 'detailed' ? (
        <Box>
          <VStack spacing={4} align="stretch">
            {filteredRocks.map((rock) => (
              <RockCardDetailed
                key={rock.id}
                rock={rock}
                users={users}
                isExpanded={isRockExpanded(rock.id)}
                onToggleExpand={() => toggleRockExpanded(rock.id)}
                onEdit={() => handleOpenModal(rock)}
                onDelete={() => handleDeleteRock(rock.id)}
                onAchieve={() => handleAchieveRock(rock.id)}
                onDrop={() => handleDropRock(rock.id)}
                onEvaluate={() => handleEvaluateClick(rock)}
                onStatusChange={(status) => handleStatusChange(rock.id, status)}
                onIssueClick={handleIssueClick}
                onResolveIssue={handleResolveIssueInline}
                refreshTrigger={refreshTrigger}
                refreshMilestonesTrigger={refreshMilestonesTrigger}
                refreshActionItemsTrigger={refreshActionItemsTrigger}
                aiScore={rockAiScores[rock.id] ?? null}
                loadMilestonesForRock={loadMilestonesForRock}
                loadActionItemsForRock={loadActionItemsForRock}
                loadIssuesForRock={loadIssuesForRock}
                subscribeToMilestonesForRock={subscribeToMilestonesForRock}
                subscribeToActionItemsForRock={subscribeToActionItemsForRock}
                subscribeToIssues={subscribeToIssues}
                saveRockUpdate={saveRockUpdate}
                loadPreviousNotes={loadPreviousNotes}
                currentUserId={currentUser?.uid}
                addMilestone={handleAddMilestone}
                updateMilestone={updateMilestone}
                toggleMilestone={handleToggleMilestone}
                addActionItem={handleAddActionItem}
                updateActionItem={updateActionItem}
                toggleActionItem={handleToggleActionItem}
                updateIssue={updateIssue}
              />
            ))}
          </VStack>
        </Box>
      ) : (
        <RockSummaryTable
          rocks={filteredRocks}
          users={users}
          onIssueClick={handleIssueClick}
          refreshTrigger={refreshTrigger}
        />
      )}

      <RockFormModal
        isOpen={isFormOpen}
        onClose={() => {
          onFormClose();
          setEditingRock(null);
        }}
        onSave={handleSaveRock}
        editingRock={editingRock}
        currentUserId={currentUser?.uid}
        users={users}
        departments={departments}
        getMilestones={loadMilestonesForRock}
      />

      <AchieveRockModal
        isOpen={isAchieveOpen}
        onClose={() => {
          onAchieveClose();
          setSelectedRockId(null);
        }}
        onConfirm={handleConfirmAchieve}
      />

      <DropRockModal
        isOpen={isDropOpen}
        onClose={() => {
          onDropClose();
          setSelectedRockId(null);
        }}
        onConfirm={handleConfirmDrop}
      />

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => {
          onDeleteClose();
          setSelectedRockId(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Rock?"
        message="Are you sure you want to delete this Rock? This action cannot be undone."
        confirmText="Delete"
        confirmColorScheme="red"
        icon="warning"
      />

      {selectedRock && (
        <RockIssuesModal
          isOpen={isIssuesOpen}
          onClose={() => {
            onIssuesClose();
            setSelectedRockForIssues(null);
            setIssues([]);
          }}
          rock={selectedRock}
          issues={issues}
          users={users}
          onAddIssue={handleAddIssue}
          onResolveIssue={handleResolveIssue}
          currentUserId={currentUser?.uid}
        />
      )}


      {/* AI Evaluation Modals */}
      {selectedRockForEval && (
        <AITermsModal
          isOpen={isTermsOpen}
          onClose={onTermsClose}
          onAccept={handleTermsAccepted}
          onDecline={onTermsClose}
        />
      )}

      <RockEvaluationLoadingModal
        isOpen={isLoadingOpen}
        onCancel={onLoadingClose}
      />

      {evaluationResult && (
        <RockEvaluationResultsModal
          isOpen={isResultsOpen}
          onClose={() => {
            onResultsClose();
            setEvaluationResult(null);
            setSelectedRockForEval(null);
            setEvaluatingRockId(null);
          }}
          result={evaluationResult}
          onShowRewrite={handleShowRewrite}
          onBackToEditing={() => {
            onResultsClose();
            setEvaluationResult(null);
            setSelectedRockForEval(null);
            setEvaluatingRockId(null);
          }}
          onOpenChat={handleOpenChat}
          hasAcceptedTerms={hasAcceptedTerms}
        />
      )}

      {selectedRockForEval && evaluationResult && (
        <RockRewriteComparisonModal
          isOpen={isRewriteOpen}
          onClose={onRewriteClose}
          originalRock={selectedRockForEval.rock}
          originalMeasurable={selectedRockForEval.measurable}
          aiRock={evaluationResult.ai_rewrite_rock || ''}
          aiMeasurable={evaluationResult.ai_rewrite_measurable || ''}
          aiExplanation={evaluationResult.ai_explanation}
          onApplySuggestion={handleApplySuggestion}
          onKeepOriginal={handleKeepOriginal}
        />
      )}

      {selectedRockForEval && (
        <RockChatModal
          isOpen={isChatOpen}
          onClose={onChatClose}
          rockId={selectedRockForEval.id}
          rockTitle={selectedRockForEval.rock}
          evaluationResult={evaluationResult}
          currentUserId={currentUser?.uid}
        />
      )}

      {piiDetectionResult && (
        <PIIWarningModal
          isOpen={isPIIWarningOpen}
          piiResult={piiDetectionResult}
          onContinue={() => {
            onPIIWarningClose();
            setPiiDetectionResult(null);
          }}
          onCancel={() => {
            onPIIWarningClose();
            setPiiDetectionResult(null);
            setSelectedRockForEval(null);
          }}
        />
      )}



      <OutstandingIssuesModal
        isOpen={showOutstandingIssuesModal}
        onClose={() => setShowOutstandingIssuesModal(false)}
        issueCount={outstandingIssuesCount}
      />

      <AddIssueModal
        isOpen={showAddIssueModal}
        onClose={() => {
          setShowAddIssueModal(false);
          setRockForIssueRequired(null);
          setPendingRockStatusChange(null);
          setAddIssueModalButtonText('Save Issue');
          setAddIssueModalContext(undefined);
        }}
        onSave={handleAddIssueFromStatusChange}
        currentUserId={currentUser?.uid}
        initialRelatedRockId={rockForIssueRequired?.id}
        submitButtonText={addIssueModalButtonText}
        context={addIssueModalContext}
      />
    </Container>
  );
}
